import { useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { Linkedin, Calendar, Mail, Phone, User as UserIcon, Briefcase, Building2, FileText, Languages as LanguagesIcon, Globe } from "lucide-react";
import { useLang } from "@/context/LangContext";

// Build the form state from the auth user. Pure function — no side effects.
function buildForm(user) {
  if (!user) return null;
  return {
    photo: user.photo || "",
    first_name: user.first_name || "",
    last_name: user.last_name || "",
    email: user.email || "",
    phone: user.phone || "",
    job_title: user.job_title || "",
    company: user.company || "",
    calendar_link: user.calendar_link || "",
    linkedin: user.linkedin || "",
    bio: user.bio || "",
    // Sprint 92an — Languages spoken + WineTech regional chapters.
    languages: user.languages || [],
    winetech_regions: user.winetech_regions || [],
  };
}

// Sprint 92an — Fixed catalogs (platform-wide, no backend seeding required).
// Languages: a short pragmatic list covering the WineTech markets +
// commonly spoken business languages. Flag emojis are purely decorative.
const SPOKEN_LANGUAGES = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "nl", label: "Nederlands", flag: "🇳🇱" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
];

// WineTech regional chapters. Portugal is live, Italy launching soon.
// Each entry is also a hint for i18n: the UI must be fully localised
// in those languages — do NOT ship French-only strings here.
const WINETECH_REGIONS = [
  { id: "winetech_france", label: "WineTech France", flag: "🇫🇷" },
  { id: "winetech_portugal", label: "WineTech Portugal", flag: "🇵🇹" },
  { id: "winetech_italia", label: "WineTech Italia", flag: "🇮🇹" },
];

export default function PersonalProfile() {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  // React 19-friendly pattern: derive state during render when the prop changes
  // (avoids `react-hooks/set-state-in-effect`). See:
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [form, setForm] = useState(() => buildForm(user));
  const [lastUserId, setLastUserId] = useState(user?.id);
  const [loading, setLoading] = useState(false);
  if (user?.id !== lastUserId) {
    setLastUserId(user?.id);
    setForm(buildForm(user));
  }

  if (!form) return <PortalLayout sidebar="member"><div className="py-20 text-center text-neutral-500">{t("portal_personalprofile_90")}</div></PortalLayout>;

  const set = (patch) => setForm({ ...form, ...patch });

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch("/auth/me", form);
      toast.success("Profil personnel mis à jour");
      if (refresh) await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur de sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const initials = `${form.first_name?.[0] || ""}${form.last_name?.[0] || ""}` || "U";
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

  return (
    <PortalLayout sidebar={user?.role === "admin" ? "admin" : "member"}>
      <div className="max-w-3xl">
        <h1 className="text-3xl font-bold text-navy md:text-4xl">{t("portal_personalprofile_01")}</h1>
        <p className="mt-1 text-neutral-600">{t("portal_personalprofile_02")}</p>

        <form onSubmit={save} className="mt-8 space-y-6">
          {/* Avatar */}
          <div className="rounded-2xl border border-neutral-100 bg-white p-6">
            <h2 className="text-lg font-bold text-navy">Photo</h2>
            <div className="mt-4 flex items-center gap-5">
              {form.photo ? (
                <img src={resolveUrl(form.photo)} alt="Avatar" className="h-24 w-24 rounded-full object-cover ring-4 ring-wine-50" data-testid="me-avatar-preview" />
              ) : (
                <div className="grid h-24 w-24 place-items-center rounded-full bg-wine-100 text-2xl font-bold text-wine-700">{initials}</div>
              )}
              <div className="flex-1">
                <ImageUpload value={form.photo} onChange={(url) => set({ photo: url })} label={t("portal_personalprofile_91")} testId="me-photo-upload" />
              </div>
            </div>
          </div>

          {/* Identité */}
          <div className="rounded-2xl border border-neutral-100 bg-white p-6">
            <h2 className="text-lg font-bold text-navy">{t("portal_personalprofile_03")}</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500"><UserIcon className="mr-1 inline h-3 w-3" />{t("portal_personalprofile_04")}</label>
                <input required value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="me-first-name" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Nom</label>
                <input required value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="me-last-name" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500"><Briefcase className="mr-1 inline h-3 w-3" /> Fonction</label>
                <input value={form.job_title} onChange={(e) => set({ job_title: e.target.value })} placeholder="Ex : CEO, Data Scientist, Œnologue…" className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="me-job-title" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500"><Building2 className="mr-1 inline h-3 w-3" /> Entreprise</label>
                <input value={form.company} onChange={(e) => set({ company: e.target.value })} placeholder="Nom de votre entreprise" className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="me-company" />
                <p className="mt-1 text-[11px] text-neutral-500">Affiché sur votre badge et lors de vos RDV 1:1 individuels.</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="rounded-2xl border border-neutral-100 bg-white p-6">
            <h2 className="text-lg font-bold text-navy">Contact</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500"><Mail className="mr-1 inline h-3 w-3" /> Email</label>
                <input required type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="me-email" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500"><Phone className="mr-1 inline h-3 w-3" />{t("portal_personalprofile_05")}</label>
                <input type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="+33 6 …" className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="me-phone" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500"><Calendar className="mr-1 inline h-3 w-3" /> Lien agenda public</label>
                <input type="url" value={form.calendar_link} onChange={(e) => set({ calendar_link: e.target.value })} placeholder="https://calendly.com/votre-nom ou https://cal.com/…" className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="me-calendar-link" />
                <p className="mt-1 text-[11px] text-neutral-500">{t("portal_personalprofile_06")}</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500"><Linkedin className="mr-1 inline h-3 w-3" /> LinkedIn</label>
                <input type="url" value={form.linkedin} onChange={(e) => set({ linkedin: e.target.value })} placeholder="https://linkedin.com/in/…" className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="me-linkedin" />
              </div>
            </div>
          </div>

          {/* Sprint 92an — Languages spoken (toggle chips) */}
          <div className="rounded-2xl border border-neutral-100 bg-white p-6">
            <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2">
              <LanguagesIcon className="h-4 w-4 text-wine-500" /> Langues parlées
            </h2>
            <p className="mt-0.5 text-xs text-neutral-500">Sélectionnez les langues dans lesquelles vous êtes à l'aise pour échanger (plusieurs choix possibles).</p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="me-languages">
              {SPOKEN_LANGUAGES.map((l) => {
                const active = (form.languages || []).includes(l.code);
                return (
                  <button
                    type="button"
                    key={l.code}
                    onClick={() => set({ languages: active ? form.languages.filter((x) => x !== l.code) : [...(form.languages || []), l.code] })}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${active ? "border-wine-500 bg-wine-500 text-white" : "border-neutral-200 bg-white text-neutral-700 hover:border-wine-300"}`}
                    data-testid={`me-language-${l.code}`}
                  >
                    <span>{l.flag}</span> {l.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sprint 92an — WineTech regional chapters (France / Portugal / Italia). */}
          <div className="rounded-2xl border border-pink-100 bg-pink-50/30 p-6">
            <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2">
              <Globe className="h-4 w-4 text-pink-600" /> Chapitres WineTech
            </h2>
            <p className="mt-0.5 text-xs text-neutral-600">Indiquez les chapitres régionaux WineTech auxquels vous êtes rattaché·e. L'interface s'affiche dans la langue de chaque chapitre.</p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="me-winetech-regions">
              {WINETECH_REGIONS.map((r) => {
                const active = (form.winetech_regions || []).includes(r.id);
                return (
                  <button
                    type="button"
                    key={r.id}
                    onClick={() => set({ winetech_regions: active ? form.winetech_regions.filter((x) => x !== r.id) : [...(form.winetech_regions || []), r.id] })}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition ${active ? "border-pink-500 bg-pink-500 text-white" : "border-neutral-200 bg-white text-neutral-700 hover:border-pink-300"}`}
                    data-testid={`me-region-${r.id}`}
                  >
                    <span>{r.flag}</span> {r.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bio */}
          <div className="rounded-2xl border border-neutral-100 bg-white p-6">
            <h2 className="text-lg font-bold text-navy"><FileText className="mr-1 inline h-4 w-4" /> Bio courte</h2>
            <textarea rows={4} value={form.bio} onChange={(e) => set({ bio: e.target.value })} placeholder={t("portal_personalprofile_92")} className="mt-3 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="me-bio" />
          </div>

          <button type="submit" disabled={loading} className="rounded-full bg-wine-500 px-8 py-3 text-sm font-semibold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="me-save">
            {loading ? "Enregistrement..." : "Enregistrer mon profil"}
          </button>
        </form>
      </div>
    </PortalLayout>
  );
}
